import {
  AnyTreeNode,
  FolderNode,
  TreeDrop,
  TreeDropAction,
  TreeDropPosition,
  TreeDropZone,
  VisibleTreeNode,
} from './types';

export const isFolder = <F, L>(
  node: AnyTreeNode<F, L>,
): node is FolderNode<F, L> => node.kind === 'folder';

export const findNodeById = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  id: string,
): AnyTreeNode<F, L> | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (isFolder(node)) {
      const found = findNodeById(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const walkTree = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  callback: (node: AnyTreeNode<F, L>, depth: number) => void,
  depth = 0,
): void => {
  for (const node of nodes) {
    callback(node, depth);
    if (isFolder(node)) {
      walkTree(node.children, callback, depth + 1);
    }
  }
};

const matchesQuery = (label: string, q: string): boolean =>
  label.toLowerCase().includes(q.toLowerCase());

const sortFoldersFirst = <F, L>(
  nodes: AnyTreeNode<F, L>[],
): AnyTreeNode<F, L>[] => {
  const folders: AnyTreeNode<F, L>[] = [];
  const leaves: AnyTreeNode<F, L>[] = [];

  for (const node of nodes) {
    if (isFolder(node)) {
      folders.push({ ...node, children: sortFoldersFirst(node.children) });
    } else {
      leaves.push(node);
    }
  }

  return [...folders, ...leaves];
};

export const filterTree = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  query: string,
  predicate?: (node: AnyTreeNode<F, L>) => boolean,
  getSearchText?: (node: AnyTreeNode<F, L>) => string,
): { nodes: AnyTreeNode<F, L>[]; forceExpand: Set<string> } => {
  const forceExpand = new Set<string>();
  const hasQuery = query.trim().length > 0;
  const hasPredicate = typeof predicate === 'function';

  if (!hasQuery && !hasPredicate) {
    return { nodes: sortFoldersFirst(nodes), forceExpand };
  }

  const filter = (
    list: AnyTreeNode<F, L>[],
    ancestors: string[],
  ): AnyTreeNode<F, L>[] => {
    const out: AnyTreeNode<F, L>[] = [];
    for (const node of list) {
      const isPredicateOk = !hasPredicate || predicate!(node);
      if (!isPredicateOk) {
        continue;
      }
      const searchable = getSearchText?.(node) ?? node.label;
      const isSelfMatch = !hasQuery || matchesQuery(searchable, query);
      if (isFolder(node)) {
        const childAncestors = [...ancestors, node.id];
        const filteredChildren = filter(node.children, childAncestors);
        const hasMatchedChild = filteredChildren.length > 0;
        if (isSelfMatch || hasMatchedChild) {
          if (hasQuery && (hasMatchedChild || isSelfMatch)) {
            ancestors.forEach((id) => forceExpand.add(id));
            forceExpand.add(node.id);
          }
          out.push({ ...node, children: filteredChildren });
        }
      } else if (isSelfMatch) {
        if (hasQuery) {
          ancestors.forEach((id) => forceExpand.add(id));
        }
        out.push(node);
      }
    }
    return out;
  };

  return { nodes: sortFoldersFirst(filter(nodes, [])), forceExpand };
};

export const flattenVisibleNodes = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  expandedIds: ReadonlySet<string>,
): VisibleTreeNode<F, L>[] => {
  const out: VisibleTreeNode<F, L>[] = [];
  const visit = (
    list: AnyTreeNode<F, L>[],
    depth: number,
    ancestorIds: string[],
    ancestorIsLast: boolean[],
  ) => {
    list.forEach((node, index) => {
      const isLast = index === list.length - 1;
      out.push({ node, depth, ancestorIds, isLast, ancestorIsLast });
      if (isFolder(node) && expandedIds.has(node.id)) {
        visit(
          node.children,
          depth + 1,
          [...ancestorIds, node.id],
          [...ancestorIsLast, isLast],
        );
      }
    });
  };
  visit(nodes, 0, [], []);
  return out;
};

export const toggleInSet = <T>(set: ReadonlySet<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

export const collectFolderIds = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  maxDepth?: number,
): Set<string> => {
  const out = new Set<string>();
  walkTree(nodes, (node, depth) => {
    if (isFolder(node) && (maxDepth === undefined || depth < maxDepth)) {
      out.add(node.id);
    }
  });
  return out;
};

type ParentedRow = { id: number; parent_id: number | null };

type BuildFolderTreeOptions<TRow extends ParentedRow, TData> = {
  getId: (row: TRow) => string;
  getLabel: (row: TRow) => string;
  getData: (row: TRow) => TData;
  compare?: (a: TRow, b: TRow) => number;
};

const groupByParent = <TRow extends ParentedRow>(rows: TRow[]) => {
  const childrenByParent = new Map<number | null, TRow[]>();

  rows.forEach((row) => {
    const siblings = childrenByParent.get(row.parent_id) ?? [];
    siblings.push(row);
    childrenByParent.set(row.parent_id, siblings);
  });

  return childrenByParent;
};

export const buildFolderTree = <TRow extends ParentedRow, TData>(
  rows: TRow[],
  options: BuildFolderTreeOptions<TRow, TData>,
): FolderNode<TData, TData>[] => {
  const { getId, getLabel, getData, compare } = options;
  const childrenByParent = groupByParent(rows);

  const buildNodes = (parent: TRow | null): FolderNode<TData, TData>[] => {
    const children = (childrenByParent.get(parent?.id ?? null) ?? []).slice();

    if (compare) {
      children.sort(compare);
    }

    return children.map((row) => ({
      id: getId(row),
      parentId: parent ? getId(parent) : null,
      label: getLabel(row),
      kind: 'folder',
      data: getData(row),
      children: buildNodes(row),
    }));
  };

  return buildNodes(null);
};

export const collectDescendantIds = <TRow extends ParentedRow>(
  rows: TRow[],
  id: number,
): number[] => {
  const childrenByParent = groupByParent(rows);

  const collect = (rowId: number): number[] => [
    rowId,
    ...(childrenByParent.get(rowId) ?? []).flatMap((child) =>
      collect(child.id),
    ),
  ];

  return collect(id);
};

const getSubtreeHeight = <F, L>(node: AnyTreeNode<F, L>): number =>
  isFolder(node) && node.children.length > 0
    ? 1 + Math.max(...node.children.map((child) => getSubtreeHeight(child)))
    : 0;

type FolderDropOptions = {
  getDepth: (id: string) => number;
  maxDepth: number;
};

export const getFolderDropAction = <F, L>(
  active: AnyTreeNode<F, L>,
  target: AnyTreeNode<F, L>,
  zone: TreeDropZone,
  options: FolderDropOptions,
): TreeDropAction | null => {
  const { getDepth, maxDepth } = options;

  if (findNodeById([active], target.id)) {
    return null;
  }

  const height = getSubtreeHeight(active);

  if (zone === 'into') {
    const isAllowed =
      target.id !== active.parentId &&
      getDepth(target.id) + 1 + height < maxDepth;

    return isAllowed ? 'into' : null;
  }

  const isAllowed =
    active.parentId === target.parentId ||
    getDepth(target.id) + height < maxDepth;

  return isAllowed ? 'reorder' : null;
};

type TreeDropParams<TRow extends ParentedRow> = {
  active: TRow;
  over: TRow;
  action: TreeDropAction;
  position: TreeDropPosition;
  compare: (a: TRow, b: TRow) => number;
};

export const buildTreeDrop = <TRow extends ParentedRow>(
  rows: TRow[],
  params: TreeDropParams<TRow>,
): TreeDrop => {
  const { active, over, action, position, compare } = params;
  const parentId = action === 'into' ? over.id : over.parent_id;
  const ids = rows
    .filter((row) => row.parent_id === parentId && row.id !== active.id)
    .sort(compare)
    .map((row) => row.id);

  if (action === 'into') {
    ids.push(active.id);
  } else {
    const overIndex = ids.indexOf(over.id);
    ids.splice(position === 'after' ? overIndex + 1 : overIndex, 0, active.id);
  }

  return { activeId: active.id, parentId, ids };
};

export const patchTreeDrop = <
  TRow extends ParentedRow & { sort_order: number },
>(
  rows: TRow[],
  drop: TreeDrop,
): TRow[] => {
  const order = new Map(drop.ids.map((id, index) => [id, index]));

  return rows.map((row) => {
    const sortOrder = order.get(row.id);
    const isActive = row.id === drop.activeId;

    if (sortOrder === undefined && !isActive) {
      return row;
    }

    return {
      ...row,
      parent_id: isActive ? drop.parentId : row.parent_id,
      sort_order: sortOrder ?? row.sort_order,
    };
  });
};

export const openContextMenuFrom = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();

  element.dispatchEvent(
    new window.MouseEvent('contextmenu', {
      bubbles: true,
      clientX: rect.right,
      clientY: rect.bottom,
    }),
  );
};
